import { useState, useEffect, useCallback, useRef } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { CONFIDENCE_DISCLOSURE } from "../lib/heroBalance";
import AccountBreakdownCard from "../components/AccountBreakdownCard";
import { toast } from "sonner";
import { Wallet, RefreshCw, Server, Link2Off } from "lucide-react";

/**
 * Format an ISO-8601 timestamp as a relative-time string ("2 minutes ago").
 *
 * Inlined rather than pulling in date-fns/formatDistanceToNow because the
 * "Last synced" indicator is the only consumer in the file and the helper
 * is trivial. Returns "just now" for the first minute, an integer
 * minute/hour/day count thereafter. Returns "—" for null / invalid input
 * (the indicator is only rendered when synced_at is truthy, but guarding
 * here keeps the helper safe to call from anywhere).
 *
 * MONAXA-REALDATA-006: sourced from ``account.synced_at`` (the field
 * added by TKT-2 / MONAXA-REALDATA-005). The cache facade writes UTC
 * ISO-8601 timestamps; we render the elapsed wall-clock window in the
 * user's local timezone.
 */
const formatRelativeFromIso = (iso) => {
  if (!iso) return "—";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "—";
  const diffMs = Date.now() - then.getTime();
  if (diffMs < 0) return "just now";  // clock skew → treat as "now"
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

/** The card renders nothing for this payload: see AccountBreakdownCard. */
const BREAKDOWN_NOT_SHOWN = { data: { status: "not_enabled" } };

const PartnerAccountPage = () => {
  const { user, isAdmin } = useAuth();
  const [account, setAccount] = useState(null);
  // STARSEED-CAPMARKETS-216 — cash movements as the partner recorded
  // them. `null` means "not fetched / unavailable", which must stay
  // distinguishable from a fetched-and-empty list.
  const [txns, setTxns] = useState(null);
  // STARSEED-CAPMARKETS-217 — balance derived from the ledger. `null`
  // means "could not load", which must stay distinct from a total of null
  // ("no usable movements") and from 0.00 ("netted to zero").
  const [ledgerBal, setLedgerBal] = useState(null);
  // STARSEED-CAPMARKETS-325 — each account separately. `undefined` means not
  // fetched, `null` means the request failed; both are distinct from the
  // payload's own `not_enabled` / `no_accounts_mapped` states.
  const [breakdown, setBreakdown] = useState(undefined);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(user?.id);
  const [loading, setLoading] = useState(true);
  // MONAXA-REALDATA-006: admin "Refresh from Partner" button state. Drives
  // the button-level spinner and the disabled state on the button itself.
  // Note: refreshFromPartner calls loadData, which toggles `loading=true`
  // and therefore re-renders the full-page "Loading partner account…" body
  // block during refresh. Acceptable UX for an admin-triggered reload.
  const [refreshing, setRefreshing] = useState(false);

  const loadUsers = async () => {
    try {
      const res = await api.get("/users");
      setUsers(res.data);
    } catch (err) { console.debug("PartnerAccountPage: loadUsers failed", err); }
  };

  /**
   * Fetch the account state, the ledger balance, the transactions and, when
   * it can be shown, the per-account card for ``uid``.
   *
   * The optional ``forceRefresh`` flag appends ``?refresh=1`` to the
   * account GET so admins can bypass the TTL cache (MONAXA-REALDATA-005).
   */
  // STARSEED-CAPMARKETS-325 — the per-account card is fetched only when it can
  // be shown: always for an admin (a preview while the switch is off), and for
  // a member only once `partner_account_breakdown` is on. The switch rides
  // /auth/me and is read fail-closed on `=== true`, like every other flag in
  // this app, so a member on an API that predates the endpoint is never sent to
  // it and a real 404 can stay an error.
  const breakdownVisible = isAdmin || user?.feature_flags?.partner_account_breakdown === true;

  // Only the latest load may write state. An admin switching members mid-load
  // otherwise sees the previous member's figures under the new member's name
  // when the earlier request finishes last.
  const latestLoad = useRef(0);

  const loadData = useCallback(async (uid, { forceRefresh = false } = {}) => {
    const loadId = ++latestLoad.current;
    setLoading(true);
    try {
      const isOwn = uid === user?.id;
      const accountPath = isOwn ? "/monaxa/account" : `/monaxa/account/${uid}`;
      const accountUrl = forceRefresh ? `${accountPath}?refresh=1` : accountPath;
      // STARSEED-CAPMARKETS-218 — every fetch carries its OWN catch, so one
      // failure cannot take down the others. Moving the ledger sections
      // outside the `!account` render gate was not enough: the account fetch
      // rejecting made `Promise.all` reject, so `setLedgerBal` never ran and
      // the sections rendered their "could not calculate" branch. The data
      // was fetched, discarded, and reported as unavailable.
      //
      // The legacy account fetch is the one that fails for these members, and
      // it is precisely the one that must not be load-bearing for the rest.
      // CAPMARKETS-231: open positions are not shown to a member, so they
      // are not fetched. CAPMARKETS-337: nor is `/monaxa/history`. It fed only
      // the Trade History tab, which is gone with the trading-fields block.
      const [accRes, txRes, balRes, breakdownRes] = await Promise.all([
        api.get(accountUrl).catch(() => null),
        api.get(isOwn ? "/monaxa/transactions?limit=100" : `/monaxa/transactions/${uid}?limit=100`)
          .catch(() => null),
        api.get(isOwn ? "/monaxa/balance" : `/monaxa/balance/${uid}`).catch(() => null),
        // A 403 is a member whose dashboard is not activated yet: the card is
        // not theirs to see, which is not a failure to load it.
        breakdownVisible
          ? api.get(isOwn ? "/monaxa/account-breakdown" : `/monaxa/account-breakdown/${uid}`)
            .catch((e) => (e?.response?.status === 403 ? BREAKDOWN_NOT_SHOWN : null))
          : Promise.resolve(BREAKDOWN_NOT_SHOWN),
      ]);
      if (loadId !== latestLoad.current) return;
      // `null` account still renders the fetch-failed state below — that
      // branch is unchanged. What changes is that it no longer takes the
      // member's own ledger data down with it.
      setAccount(accRes ? accRes.data : null);
      setTxns(txRes ? txRes.data : null);
      setLedgerBal(balRes ? balRes.data : null);
      setBreakdown(breakdownRes ? breakdownRes.data : null);

      // State is set BEFORE this throw, deliberately. The ledger sections
      // must render even when the legacy account fetch failed — but
      // `refreshFromPartner` still has to learn that it failed, or the admin
      // "Refresh from Partner" button reports success on a failed refresh.
      // The original code got this right by letting Promise.all reject; that
      // also discarded the ledger data. Both properties now hold.
      if (!accRes) throw new Error("account fetch failed");
    } catch (e) {
      if (loadId !== latestLoad.current) return;
      toast.error("Failed to load partner account");
      // Re-throw so callers (e.g. refreshFromPartner) can distinguish a
      // failed fetch from a successful one. Without this, the manual
      // refresh path would always report success even on GET failure.
      throw e;
    } finally {
      if (loadId === latestLoad.current) setLoading(false);
    }
  }, [user?.id, breakdownVisible]);

  useEffect(() => {
    if (isAdmin) loadUsers();
  }, [isAdmin]);

  useEffect(() => {
    // loadData re-throws on failure so refreshFromPartner can detect it,
    // but the initial mount / user-switch path has no extra handling to
    // do — the inner catch already toasted. Swallow the rejection here
    // to avoid an unhandled-promise warning in the console.
    if (selectedUserId) loadData(selectedUserId).catch(() => {});
  }, [selectedUserId, loadData]);

  /**
   * Admin-only manual refresh path. Bypasses the TTL via ``?refresh=1``
   * on the account GET. Shows a button-level spinner via
   * ``refreshing`` (drives the icon spin + label swap). Note that
   * loadData also toggles ``loading=true``, which re-renders the
   * full-page "Loading partner account…" body during the fetch — the
   * page's sections are temporarily replaced.
   */
  const refreshFromPartner = async () => {
    if (!isAdmin || refreshing) return;
    setRefreshing(true);
    try {
      await loadData(selectedUserId, { forceRefresh: true });
      toast.success("Refreshed from Partner");
    } catch {
      toast.error("Refresh from Partner failed");
    } finally {
      setRefreshing(false);
    }
  };

  // MONAXA-REALDATA-006: render the demo badge + disclosure only when the
  // current account is actually flagged ``is_demo``. Pre-006 these were
  // page-level constants that claimed "this page is demo data" — false
  // after MONAXA-REALDATA-005 cut the surface over to real Monaxa. The
  // Track 0 (FORGE-20260522-001) ``is_demo`` field is the source of truth.
  const isDemoAccount = account && account.is_demo === true;
  // ``synced_at`` may be absent for legacy backfilled rows that never went
  // through the cache facade; we only render the indicator when present.
  const hasSyncedAt = account && account.synced_at;
  const isStale = account && account.stale === true;
  const isUnlinked = account && account.not_linked === true;
  /**
   * STARSEED-CAPMARKETS-169 — linked, and the legacy `/monaxa/account` fetch
   * cannot serve them. Permanent under the MAM shape (CAPMARKETS-133/182: one
   * Monaxa user owns all nine accounts), so this is a steady state, not a
   * pending one.
   *
   * It renders NOTHING. That is the whole point of the state: the member's
   * figures are already on this page, above, from `/monaxa/balance` and
   * `/monaxa/account-breakdown`, which read the CAPMARKETS-114 mapping. Every
   * empty-state card below would contradict them — which is the bug: measured
   * read-only on prod 2026-09-22, all 4 mapped members saw their money and
   * "No partner account linked" at the same time.
   *
   * Read fail-closed on `=== true`, like every other server flag on this page,
   * so an API that predates the field renders exactly as it does today.
   */
  const partnerFetchUnavailable =
    account && account.partner_fetch_unavailable === true;
  /**
   * STARSEED-CAPMARKETS-105 AC7 — a LINKED member whose partner side
   * reports zero accounts.
   *
   * The service's empty-accounts branch (``_build_account_block`` with
   * ``raw_account=None``, services/monaxa_service.py) returned a fully
   * shaped account block: every numeric field at the model default of
   * ``0.0``, ``is_demo=False``, and ``account_number`` deliberately left
   * as an empty-string sentinel. STARSEED-CAPMARKETS-349 removed that
   * branch with the per-member fetch, so nothing writes the shape now. A
   * row cached before then would still carry it, and the sentinel is still
   * defined as ``EMPTY_ACCOUNT_SENTINEL_NUMBER`` in that module, so this
   * guard stays until the cache's readers go. Before STARSEED-CAPMARKETS-337
   * that shape, missing this branch, fell through to the populated block and
   * rendered "Balance $0.00 / Equity $0.00" — telling a member their money is
   * zero when the truth is that their account isn't reporting yet. The block
   * is gone, so it would now render nothing, and the member would lose the
   * explanation below. A balance of nothing and no balance at all are
   * different facts, and only one of them is this member's.
   *
   * This is not a hypothetical: prod on 2026-07-30 has 13 members, 1
   * countersigned agreement and 0 confirmed funds, so the first member
   * to be mapped is likely to hit exactly this state before any account
   * exists on the partner side.
   *
   * Reading the documented sentinel is a stopgap. CAPMARKETS-103 should
   * replace it with an explicit server-side flag when it lands the
   * ``accounts[]`` payload; until then this is the only signal the
   * server sends, and it is documented in the service rather than
   * inferred here.
   */
  // `!isUnlinked` is belt-and-braces: the not-linked state is matched EARLIER
  // in the render chain below and renders its own panel, so deleting either
  // that branch or this term is visible to a member and to the render tests.
  //
  // `partnerFetchUnavailable` is deliberately NOT repeated here
  // (STARSEED-CAPMARKETS-337). Its branch renders nothing, and a term that
  // repeats a branch which renders nothing makes deleting that branch
  // invisible. Reviewing PR #2654, an agent deleted the branch and all 47
  // render tests stayed green, while a linked member's payload fell through
  // this term to the trading-fields block that 337 removed. Without the term,
  // the chain's order is the one guard, and deleting the branch sends that
  // payload (it carries no account number) into the no-accounts card below,
  // under the member's own figures. `PartnerAccountPage.render.test.jsx`'s
  // CAPMARKETS-337 block goes red on exactly that.
  const hasNoAccountsReporting =
    account && !isUnlinked && !String(account.account_number || "").trim();

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="partner-account-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-serif text-foreground flex items-center gap-3">
              <Server className="text-gold" /> Partner Account
              {isDemoAccount && (
                <Badge
                  variant="outline"
                  className="text-amber-700 border-amber-300 bg-amber-50"
                  data-testid="partner-demo-badge"
                >
                  Demo data
                </Badge>
              )}
            </h1>
            {isDemoAccount && (
              <p className="text-sm text-muted-foreground mt-1" data-testid="partner-demo-disclosure">
                This account is flagged as demo and is excluded from Combined
                Balance and Platform AUM rollups.
              </p>
            )}
            {hasSyncedAt && !isUnlinked && (
              <p
                className={`text-xs mt-1 ${isStale ? "text-amber-600" : "text-muted-foreground"}`}
                data-testid="partner-synced-at"
              >
                Last synced: {formatRelativeFromIso(account.synced_at)}
                {isStale && " (Partner is unreachable)"}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {isAdmin && users.length > 0 && (
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger className="w-64" data-testid="partner-user-select">
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {/* STARSEED-CAPMARKETS-169: `!partnerFetchUnavailable` is NOT
                belt-and-braces here, unlike the terms in
                `hasNoAccountsReporting`. Before this ticket these members
                returned `{not_linked: true}`, so `isUnlinked` hid this button.
                Reporting them linked un-hides it — and for them the forced
                `?refresh=1` round trip can never resolve (`GET /rest/users/{id}`
                404s permanently under the MAM shape), while `refreshFromPartner`
                still toasts "Refreshed from Partner" because the GET itself
                succeeded. An admin would get a green success on a refresh that
                did nothing, every time. Hidden for the same reason it is hidden
                for a not-linked member: there is nothing this control can do.
                (`/code-review`, verified in jsdom.) */}
            {isAdmin && !isUnlinked && !partnerFetchUnavailable && (
              <Button
                variant="outline"
                onClick={refreshFromPartner}
                disabled={refreshing || loading}
                data-testid="refresh-from-partner-btn"
              >
                <RefreshCw
                  size={16}
                  className={`mr-1 ${refreshing ? "animate-spin" : ""}`}
                />
                {refreshing ? "Refreshing…" : "Refresh from Partner"}
              </Button>
            )}
          </div>
        </div>

        {/* STARSEED-CAPMARKETS-218 — rendered OUTSIDE the account gate.
            These two read `partner_account_mappings` and the CAPMARKETS-209
            ledger, not the legacy `users.monaxa_user_id` account fetch. That
            fetch returns nothing for these members, so gating on it hid the
            member's own transactions and balance behind a "Could not load
            partner account" error — the data was fine and unreachable.
            A page must not withhold what it CAN show because of what it
            cannot. */}
        {!loading && (
          <div className="space-y-4 mb-6">
            {/* STARSEED-CAPMARKETS-217 — the balance the member is actually
                shown, now that statement screenshots have stopped. Derived
                here from their own movements; the partner's balance fields
                read 0.00 on every account (CAPMARKETS-190). Four states, kept
                apart because collapsing any pair states a falsehood about
                someone's money:
                  null           could not load
                  no_accounts    nothing attached yet
                  total === null no usable movements (NOT zero)
                  a number       including corrections nobody has explained  */}
            <Card className="bg-surface border-platinum/10" data-testid="ledger-balance-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  Balance from your transaction history
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {ledgerBal === null ? (
                  <p className="text-sm text-muted-foreground" data-testid="ledger-bal-unavailable">
                    Could not be calculated right now. This is not a balance of
                    zero — please try again shortly.
                  </p>
                ) : ledgerBal.status === "no_accounts_mapped" ? (
                  <p className="text-sm text-muted-foreground" data-testid="ledger-bal-unmapped">
                    No partner account is connected to your profile yet.
                  </p>
                ) : ledgerBal.total === null || ledgerBal.total === undefined ? (
                  <p className="text-sm text-muted-foreground" data-testid="ledger-bal-no-movements">
                    We have no usable movements on your accounts yet, so there is
                    no figure to show. That is not a balance of zero.
                  </p>
                ) : (
                  <>
                    <p className="text-3xl font-semibold text-gold" data-testid="ledger-bal-total">
                      {ledgerBal.total.toLocaleString(undefined, {
                        style: "currency", currency: "USD",
                      })}
                    </p>
                    <p className="text-sm text-muted-foreground" data-testid="ledger-bal-disclosure">
                      <strong>Estimate.</strong>{" "}
                      {CONFIDENCE_DISCLOSURE.ledger_derived.detail}
                    </p>
                    {ledgerBal.includes_unexplained_corrections && (
                      <p className="text-xs text-muted-foreground" data-testid="ledger-bal-corrections">
                        This figure includes partner adjustments that are still
                        being reconciled, so it may change.
                      </p>
                    )}
                    {ledgerBal.as_of && (
                      <p className="text-xs text-muted-foreground" data-testid="ledger-bal-asof">
                        Calculated {new Date(ledgerBal.as_of).toLocaleString()}
                      </p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
            {/* STARSEED-CAPMARKETS-325 — where the money in each account is:
                moved into the pool, in the account now, last statement. Sits
                directly under the Estimate because it answers the question
                that figure raises: why it does not move day to day. */}
            <AccountBreakdownCard breakdown={breakdown} />
              {/* STARSEED-CAPMARKETS-216 — cash movements exactly as the
                  partner recorded them. NOT the CAPMARKETS-211 reconstruction,
                  which is 7-9% out for most members and stays in shadow until
                  CAPMARKETS-212's bar is met. Three states are rendered
                  distinctly, because collapsing them is CAPMARKETS-181:
                    null                -> could not load (partner unreachable)
                    no_accounts_mapped  -> nothing attached yet
                    available + 0 rows  -> attached, genuinely no movements  */}
              <div data-testid="ledger-transactions-section">
                <Card className="bg-surface border-platinum/10">
                  <CardContent className="p-0">
                    {txns === null ? (
                      <p className="p-6 text-center text-muted-foreground" data-testid="txns-unavailable">
                        Transactions could not be loaded right now. This is not a
                        statement that you have none — please try again shortly.
                      </p>
                    ) : txns.status === "no_accounts_mapped" ? (
                      <p className="p-6 text-center text-muted-foreground" data-testid="txns-unmapped">
                        No partner account is connected to your profile yet, so there
                        is nothing to show here. This is not a statement about your
                        activity.
                      </p>
                    ) : txns.transaction_count === 0 ? (
                      <p className="p-6 text-center text-muted-foreground" data-testid="txns-empty">
                        No transactions recorded on your account.
                      </p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="border-b border-platinum/10 text-muted-foreground">
                            <tr>
                              <th className="text-left p-3">Date</th>
                              <th className="text-left p-3">Type</th>
                              <th className="text-left p-3">Account</th>
                              <th className="text-right p-3">Amount</th>
                              <th className="text-left p-3">Status</th>
                            </tr>
                          </thead>
                          <tbody data-testid="txns-rows">
                            {txns.transactions.map((t) => (
                              <tr key={t.id} className="border-b border-platinum/5 hover:bg-platinum/5">
                                <td className="p-3 text-xs text-muted-foreground">
                                  {t.processed_at
                                    ? new Date(t.processed_at).toLocaleDateString()
                                    : "\u2014"}
                                </td>
                                <td className="p-3 font-medium">{t.type || "\u2014"}</td>
                                <td className="p-3 text-xs text-muted-foreground">{t.account_login}</td>
                                <td className="p-3 text-right">
                                  {/* An absent amount is NOT zero. */}
                                  {t.processed_amount === null || t.processed_amount === undefined
                                    ? "\u2014"
                                    : t.processed_amount.toLocaleString(undefined, {
                                        style: "currency", currency: t.processed_currency || "USD",
                                      })}
                                </td>
                                <td className="p-3 text-xs text-muted-foreground">{t.status || "\u2014"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading partner account…</div>
        ) : !account ? (
          // After loading=false but account=null: the fetch failed.
          // Show an explicit error state (not a pseudo-loading message)
          // so admins can tell apart "still loading" vs "fetch failed."
          // The page header already exposes a "Refresh from Partner" button
          // (admin-only) for retry; no in-content CTA needed.
          <div className="text-center py-12 text-muted-foreground" data-testid="partner-fetch-failed-state">
            Could not load partner account. Use the &ldquo;Refresh from Partner&rdquo; button to retry.
          </div>
        ) : partnerFetchUnavailable ? (
          // STARSEED-CAPMARKETS-169 — renders nothing, deliberately. Placed
          // BEFORE `isUnlinked` and `hasNoAccountsReporting` so neither can
          // claim a member this state owns. `null` rather than an omitted
          // branch so the intent is legible: the absence is the feature, and a
          // future edit adding a card here has to delete this comment first.
          //
          // STARSEED-CAPMARKETS-337: this order is now the ONLY thing keeping
          // a linked member out of the no-accounts card. `hasNoAccountsReporting`
          // no longer repeats the flag (see there for why), and the render test
          // that names this branch fails when it is deleted.
          null
        ) : isUnlinked ? (
          // MONAXA-REALDATA-005a: dedicated empty-state for the
          // ``{ not_linked: true }`` JSON sentinel returned by /monaxa/account
          // when ``users.monaxa_user_id`` is unset. Kept as a SECOND
          // distinct branch (not merged into ``loading || !account``)
          // because the two states are semantically different:
          // loading-failed is transient; not-linked is persistent until
          // an admin links the user. No CTA button — the user cannot
          // self-link; admin linking is a separate ticket (forge route
          // ``PATCH /api/users/{user_id}/monaxa-link`` exists per PR #15
          // but no UI driver yet).
          <Card className="bg-surface border-platinum/10" data-testid="partner-not-linked-empty-state">
            <CardContent className="flex flex-col items-center text-center py-16 px-6">
              <Link2Off className="text-muted-foreground mb-4" size={48} />
              <h2 className="text-xl font-serif text-foreground mb-2">
                No partner account linked
              </h2>
              {/* STARSEED-CAPMARKETS-169 AC5. This used to promise "balance,
                  equity, and transaction history". CAPMARKETS-231 settled on
                  2026-08-31 that no equity or open-trade figure is shown to a
                  member — the partner asked for it to stop, and EchoSense
                  presents a managed holding, not a trading terminal. Promising
                  a figure the product will never show is the same defect one
                  tense earlier. The word `equity` is asserted absent by
                  `PartnerAccountPage.render.test.jsx` so it cannot come back. */}
              <p className="text-sm text-muted-foreground max-w-md">
                Your EchoSense account isn&apos;t linked to a partner account
                yet. Once linked, your balance and transaction history will
                appear here. An admin can link your account from the admin
                panel.
              </p>
            </CardContent>
          </Card>
        ) : hasNoAccountsReporting ? (
          // STARSEED-CAPMARKETS-105 AC7: linked, but the partner reports no
          // accounts yet. A THIRD distinct branch, deliberately not merged
          // with not-linked above: "we have no link to your account" and
          // "your account exists but has nothing to report" are different
          // facts and lead a member to different next actions. Rendering
          // either one as a $0 balance would be the worst of the three.
          //
          // No currency figure appears anywhere in this branch — that
          // absence is asserted by partnerAccountStates.test.js, because a
          // future edit adding a "$0.00" placeholder here would silently
          // reintroduce exactly the misinformation this branch exists to
          // prevent.
          <Card className="bg-surface border-platinum/10" data-testid="partner-no-accounts-empty-state">
            <CardContent className="flex flex-col items-center text-center py-16 px-6">
              <Wallet className="text-muted-foreground mb-4" size={48} />
              <h2 className="text-xl font-serif text-foreground mb-2">
                No account activity to report yet
              </h2>
              {/* STARSEED-CAPMARKETS-162. Partner rows are now served only to
                  the member they are mapped to, so a LINKED member whose cached
                  rows are all withheld reaches this branch too: no
                  account_number, and the API says `balance_complete: false`
                  (pinned through the real route by the 163 test
                  `test_a_member_whose_every_row_is_withheld_reaches_the_no_accounts_shape`).
                  "Nothing is missing" would contradict that payload, and "they
                  haven't opened a trading account" may be false, so this copy
                  states only what the payload supports and promises nothing.
                  The final wording, and whether the heading fits this case, is
                  CAPMARKETS-298's to shape. */}
              {account.balance_complete === false ? (
                <p className="text-sm text-muted-foreground max-w-md" data-testid="partner-no-accounts-incomplete">
                  Your EchoSense account is linked to the partner, but your
                  partner account information is not complete here, so no
                  figure is shown. This does not mean your partner balance is
                  zero.
                </p>
              ) : (
                // STARSEED-CAPMARKETS-337. This used to go on "Balance, equity
                // and history will appear here once they do." Nothing appears
                // here any more: the block that would have shown them is gone,
                // and equity is never shown to a member (231, 325 AC9). The
                // sentence was deleted and nothing was written in its place;
                // the rest of this copy is 298's to shape.
                <p className="text-sm text-muted-foreground max-w-md">
                  Your EchoSense account is linked to the partner, but they
                  haven&apos;t opened a trading account for you yet. Nothing is
                  missing and no action is needed from you.
                </p>
              )}
              {hasSyncedAt && (
                <p className="text-xs text-muted-foreground mt-4" data-testid="partner-no-accounts-synced-at">
                  Last checked with the partner: {formatRelativeFromIso(account.synced_at)}
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          // STARSEED-CAPMARKETS-337 — a legacy cached account row (an account
          // number and trading-account fields) renders nothing here. The block
          // that was here showed Balance / Equity / Free Margin, an Account
          // Details card (platform, leverage, server, a "Total Deposits" that
          // was really the partner's net, a "Total Withdrawals" hard-coded to
          // 0.0) and a Trade History tab. Members are never shown
          // trading-account fields, trades, trade history or open P&L, and
          // equity beside balance is open P&L by subtraction (231, 325 AC9).
          // Their partner figures come from the partner API, through the
          // Estimate and the per-account card above (357).
          //
          // Removed, not hidden. Nothing reached it (338 S1: `monaxa_accounts`
          // held 0 documents on 2026-09-23, and every mapped member is
          // `partner_fetch_unavailable`), and a hidden block is one flag away
          // from shown. The render test's CAPMARKETS-337 block walks every
          // member state and fails if a trading field comes back in any of them.
          null
        )}
      </div>
    </DashboardLayout>
  );
};

export default PartnerAccountPage;
